import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { ContentBlock } from '../LayoutPrimitives';
import { IconPhotoGallery } from '../icons/Icons';
import Heading from '../Heading';
import Button from '../Button';
import MediaGalleryModal from '../MediaGalleryModal';
import * as PartnerIcons from '../icons/partners/PartnerIcons';

import LayoutSm from './LayoutSm';
import LayoutMd from './LayoutMd';
import { PHOTO_OBJECT_TYPE } from './types';

import { useWindowSize } from '../../../hooks';
import { BREAKPOINTS, STATUS } from '../../../constants';

const { IDLE, PENDING, ERROR } = STATUS;

export default function HeroImageGalleryTall({
    galleryImages = [],
    heroImage = {},
    title,
    partnerAgency,
    locationState,
    totalCampgrounds = 0,
    totalActivities = 0,
    totalPOI = 0,
    onCampgroundsClick,
    onActivitiesClick,
    onPOIClick,
    inventoryListButtons,
    status,
    hasThumbnailStrip,
    className,
}) {

    function GovAgency(name) {
        const Partners = {
            BEP: {icon: <PartnerIcons.BEP />, name: "Bureau of Engraving and Printing (BEP) |"},
            BLM: {icon: <PartnerIcons.BLM />, name: "Bureau of Land Management (BLM) |"},
            BOR: {icon: <PartnerIcons.DOI />, name: "Bureau of Reclamation |"},
            DOT: {icon: <PartnerIcons.DOT />, name: "U.S. Department of Transportation (USDOT) |"},
            NPS: {icon: <PartnerIcons.NPS />, name: "National Park Service (NPS) |"},
            NOAA: {icon: <PartnerIcons.NOAA />, name: "National Oceanic and Atmospheric Administration (NOAA) |"},
            Smithsonian: {icon: <PartnerIcons.Smithsonian />, name: "Smithsonian |"},
            TRE: {icon: <PartnerIcons.TRE />, name: "Department of The Treasury |"},
            TVA: {icon: <PartnerIcons.TVA />, name: "Tennessee Valley Authority |"},
            USACE: {icon: <PartnerIcons.USACE />, name: "Army Corps of Engineers (USACE) |"},
            USFS: {icon: <PartnerIcons.ForestService />, name: "U.S. Forest Service (USFS) |"},
            FS: {icon: <PartnerIcons.ForestService />, name: "U.S. Forest Service (USFS) |"},
            USFWS: {icon: <PartnerIcons.FWS />, name: "U.S. Fish & Wildlife (USFWS) |"},
            FWS: {icon: <PartnerIcons.FWS />, name: "U.S. Fish & Wildlife (USFWS) |"},
            none: ''
        }

        return Partners[name] || Partners.none
    }

    const [isModalOpen, setIsModalOpen] = useState(false);
    const { width = 0 } = useWindowSize();
    const images = Array.isArray(galleryImages) ? [...galleryImages] : [];
    const agencyInfo = GovAgency(`${partnerAgency}`);
    const hasInventory = (totalCampgrounds || totalActivities || totalPOI);
    const InventoryItem = ({text, onClick}) => {
        if(inventoryListButtons){
            return (
                <Button
                    onClick={onClick}
                    appearance="link" >
                    {text}
                </Button>
            );
        }
        return text;
    };

    let galleryImagesToShow = [];

    if (!isEmpty(heroImage)) {
        images.unshift(heroImage);
        // prepend hero image to the gallery
        galleryImagesToShow.push(heroImage);
    }

    galleryImagesToShow = galleryImagesToShow.concat(galleryImages);

    const HeroTextContent = () => {
        return (
            <div className={cx(
                "sarsa-hero-image-gallery-body",
                {'sarsa-hero-image-gallery-body-with-multi-image': galleryImagesToShow.length > 1 && width >= BREAKPOINTS.md }
                )}>
                <div className="sarsa-hero-image-gallery-location-info">
                    {agencyInfo.icon} {agencyInfo.name} {locationState}
                </div>
                <Heading
                    headingLevel={1}
                    appearance="h1"
                    className={cx('sarsa-hero-image-gallery-title', {
                        'sarsa-hero-image-gallery-long-title': title.length > 30
                    })}>
                    {title}
                </Heading>
                {hasInventory >= 1 && (
                    <ul className="sarsa-hero-image-gallery-inventory-list">
                        {totalCampgrounds > 0 && (
                            <li>
                                <InventoryItem
                                    text={`${totalCampgrounds} ${totalCampgrounds > 1 ? "Campgrounds" : "Campground"}`}
                                    onClick={onCampgroundsClick} />
                            </li>
                        )}
                        {totalActivities > 0 && (
                            <li>
                                <InventoryItem
                                    text={`${totalActivities}  ${totalActivities > 1 ? "Activities" : "Activity"}`}
                                    onClick={onActivitiesClick} />
                            </li>
                        )}
                        {totalPOI > 0 && (
                            <li>
                                <InventoryItem
                                    text={`${totalPOI} ${totalPOI && totalPOI > 1 ? "Points of Interest" : "Point of Interest"}`}
                                    onClick={onPOIClick} />
                            </li>
                        )}
                    </ul>
                )}
            </div>
        )
    }

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    // RENDER LOGIC
    //
    // Case: API request is not finished - show grey box
    if (status === PENDING || status === IDLE) {
        return (
            <div className={cx('sarsa-hero-image-gallery-tall', className)}>
                <LayoutSm />
            </div>
        );
    }

    // Case: API error, or success but no images - show unavailable image
    if (status === ERROR || !images || images.length === 0) {
        return (
            <div className={cx('sarsa-hero-image-gallery-tall', className)}>
                <ContentBlock>
                    <HeroTextContent />
                </ContentBlock>
                <LayoutSm showUnavailableImage />
            </div>
        );
    }

    // Case: Less than 3 images - show single image
    if (images.length < 3) {
        return (
            <div className={cx('sarsa-hero-image-gallery-tall', className)}>
                <ContentBlock>
                    <HeroTextContent />
                </ContentBlock>
                <LayoutSm image={images[0]} />
            </div>
        );
    }

    // Case: HAPPY PATH 3+ IMAGES!
    return (
        <div>
            <div className={cx('sarsa-hero-image-gallery-tall', className)}>
                <ContentBlock>
                    <HeroTextContent />

                    <Button
                        size="xs"
                        appearance="solid-white"
                        iconBeforeElement={<IconPhotoGallery />}
                        onClick={openModal}
                        className="view-more-button"
                    >
                        View Photos
                    </Button>
                </ContentBlock>

                {width >= BREAKPOINTS.md && (
                    <LayoutMd images={images} />
                )}

                {width < BREAKPOINTS.md && <LayoutSm image={images[0]} />}
            </div>

            <MediaGalleryModal
                images={galleryImagesToShow}
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                activeIndex={0}
                hasThumbnailStrip={hasThumbnailStrip}
            />
        </div>
    );
}

HeroImageGalleryTall.propTypes = {
    galleryImages: PropTypes.arrayOf(PHOTO_OBJECT_TYPE),
    heroImage: PHOTO_OBJECT_TYPE,

    /** API fetch status */
    status: PropTypes.oneOf(['IDLE', 'PENDING', 'ERROR', 'SUCCESS']),

    /** Shows the thumbnail strip at the bottom of the open gallery */
    hasThumbnailStrip: PropTypes.bool,

    /** Title of the page/hero image */
    title: PropTypes.string,

    /**  Managing government agency */
    partnerAgency:PropTypes.oneOf(['BEP', 'BLM', 'DOI', 'DOT', 'NPS', 'NOAA', 'Smithsonian', 'TRE', 'TVA', 'USACE', 'USFS', 'FS', 'USFWS', 'FWS']),

    /** The state where this inventory is located */
    locationState: PropTypes.string,

    /** Total number of campgrounds */
    totalCampgrounds: PropTypes.number,

    /** Total number of activities */
    totalActivities: PropTypes.number,

    /** Total number of POI */
    totalPOI:PropTypes.number,

    /** On Click function for Campgrounds */
    onCampgroundsClick: PropTypes.func,

    /** On Click function for Activities */
    onActivitiesClick: PropTypes.func,

    /** On Click function for POI */
    onPOIClick: PropTypes.func,

    /** Inventory list renders as buttons */
    inventoryListButtons: PropTypes.bool,

    /** Additional className added to the outer container */
    className: PropTypes.string,
};

HeroImageGalleryTall.defaultProps = {
    hasThumbnailStrip: true,
};
